
import { SanityDocument as SanityClientDocument } from '@sanity/client';
import { Component, mixins, Prop, Watch } from 'nuxt-property-decorator';
import { ISanityObjects, SanityObjectSchema } from '~/types/sanity-objects';
import SanityPromoItemNav from '~/mixins/SanityPromoItemNav';
import { TRefDoc } from '../Sanity/PromoCarouselSlide.vue';

@Component({
  inheritAttrs: false,
})
export default class MoreFilmsSlide extends mixins(SanityPromoItemNav) {
  @Prop(Object)
  readonly item!: ISanityObjects[SanityObjectSchema.PROMO_CAROUSEL_ITEM];
  @Prop(Number)
  readonly slideAspectRatio?: number;
  @Prop(Number)
  readonly slideWidth?: number;
  @Prop({ type: Boolean, default: false })
  readonly portraitMode!: boolean;

  @Watch('item.documentReference._ref', { immediate: true })
  handleDocRefChange(
    current: string | undefined,
    previous: string | undefined,
  ) {
    if (current && current !== previous) {
      this.$sanityLookup.fetchReference({ _id: current });
    }
  }

  get documentRef() {
    const docRefId = this.item?.documentReference?._ref;
    return docRefId
      ? this.$sanityLookup.getDocumentFromReference<
          SanityClientDocument<TRefDoc & { title?: string }>
        >({ _ref: docRefId })
      : {
          isLoading: false,
          hasError: false,
        };
  }

  get defaultAspectRatio() {
    return 16 / 9;
  }

  get defaultWidth() {
    return 1920;
  }

  get aspectRatio() {
    return this.slideAspectRatio !== undefined
      ? this.slideAspectRatio
      : this.defaultAspectRatio;
  }

  get ariaLabel() {
    const title = this.documentRef?.data?.title;
    return title
      ? this.$t('components.films.moreFilmsSlide.ctaWithTitle', { title })
      : this.$t('components.films.moreFilmsSlide.cta');
  }

  get logo() {
    return this.documentRef?.data?.promoCard?.logo;
  }

  get height() {
    return Math.round(this.width * (1 / this.aspectRatio));
  }

  get width() {
    return this.slideWidth !== undefined ? this.slideWidth : this.defaultWidth;
  }

  get isLoading() {
    return this.documentRef?.isLoading;
  }

  get imageUrl() {
    const img = this.portraitMode
      ? this.documentRef?.data?.promoCard?.heroImagePortrait
      : this.documentRef?.data?.promoCard?.heroImageLandscape;
    if (!img) {
      return;
    }

    return this.$sanityImage
      .urlFor(img)
      .width(this.width)
      .height(this.height)
      .fit('crop')
      .url();
  }

  get overline() {
    return this.documentRef?.data?.genres || [];
  }
}
